import {BetType} from '@/api/player-api/models/bet-type'
import {BetslipRiskInfoDto} from '@/api/player-api/models/betslip-risk-info-dto'
import {CreateBetslipDto} from '@/api/player-api/models/create-betslip-dto'
import {CreateBetslipSelectionDto} from '@/api/player-api/models/create-betslip-selection-dto'
import {CreateBetslipSystemDto} from '@/api/player-api/models/create-betslip-system-dto'
import {RiskLimitDto} from '@/api/player-api/models/risk-limit-dto'
import {SystemRiskInfoDto} from '@/api/player-api/models/system-risk-info-dto'
import {BetslipData} from '@/features/betslip/betslip-create/lib/models/betslip-data'
import {BetslipSelection} from '@/features/betslip/betslip-create/lib/models/betslip-selection'
import {SystemStrategy} from '@/features/betslip/betslip-create/lib/strategies/implementations/system-strategy'
import {BetBuilderSystemStrategy} from '@/features/betslip/betslip-create/lib/strategies/implementations/betbuilder-system-strategy'
import {CombinationsCalculator} from '@/features/betslip/betslip-create/helpers/combinations-calculator'
import {ValidCombinationsCalculator} from '@/features/betslip/betslip-create/helpers/valid-combinations-calculator'
import {MarketOutcome, offerSelection, pairKey} from './demo-offer'

/**
 * A stand-in for the backend's `betslipRiskInfo` reply until the real one exists.
 *
 * It has the backend's contract: the same `CreateBetslipDto` the site sends
 * (`BetslipConverterService.createPayload`) in, a `BetslipRiskInfoDto` out. Swapping it for
 * `BetslipWsApi.betslipRiskInfo` changes the transport and nothing else.
 *
 * The formula is betslip-demo's (`server/liability.mjs`, ADR 0001): a Liability Limit is the
 * largest loss the operator accepts on a market-outcome pair, so the largest stake is
 * `limit / (odds − 1)`. A Single, a Combo and a Bet Builder Combo are bound by the smallest of
 * their Selections' own ceilings, never by one priced on combined odds — as the player's betslip
 * is (`player-liability-mock.ts`). A System Option is still priced on its odds per unit of stake.
 * The real backend also counts the player's open Betslips and their limit group's totals;
 * this mock has neither, so it prices every Betslip as the player's first.
 *
 * The shape follows the Betslip Type: the whole Betslip for a Single, a Combo and a Bet
 * Builder Combo; one ceiling per Selection for a Multi-single; one per System Option for a System.
 */

export enum RiskInfoStatus {
	ready = 'ready',
	pending = 'pending',
	failed = 'failed',
	loggedOut = 'loggedOut'
}

export interface LiabilityLimit extends MarketOutcome {
	/** Auto-accept tier. 0 means the operator left it unset — no bound, not a bound of 0. */
	liabilityLimit: number
	/** VIP tier. */
	vipLiabilityLimit: number
}

type LimitKey = 'liabilityLimit' | 'vipLiabilityLimit'

/** A Selection as the backend sees it once it has looked the payload's `oddsId` up in its offer. */
interface PricedSelection {
	selection: BetslipSelection
	outcomeKey: string
}

/** `upperLimit` 0 is how "no ceiling" reaches the frontend. */
export const NO_CEILING: RiskLimitDto = {upperLimit: 0, vipUpperLimit: 0, upperPayLimit: 0}

function floor2(value: number): number {
	return Math.floor(value * 100) / 100
}

/** The largest stake whose loss still fits under the Liability Limit. */
export function ceilingFor(odds: number, limit: number): number {
	if (!(limit > 0)) {
		return 0
	}

	return floor2(limit / Math.max(odds - 1, 0.01))
}

/**
 * The Liability Limit that binds a set of Selections: the smallest one configured across the
 * pairs they carry. When a System Option wins, its payout is a loss on every pair it carries at
 * once, so the stake must fit under each pair's limit — that is, under the smallest one. A pair
 * left unset drops out of the comparison; with none set the result is 0, "no ceiling".
 */
export function tightestLimit(
	limits: LiabilityLimit[],
	outcomes: {marketId: number, outcomeKey: string}[],
	key: LimitKey
): number {
	const byPair = new Map(limits.map((limit) => [pairKey(limit), limit]))
	const configured = outcomes
		.map((outcome) => byPair.get(pairKey(outcome))?.[key] ?? 0)
		.filter((value) => value > 0)

	return configured.length === 0 ? 0 : Math.min(...configured)
}

/** Both acceptance tiers arrive together, each priced off its own tier's limit. */
function riskLimit(limits: LiabilityLimit[], odds: number, selections: PricedSelection[]): RiskLimitDto {
	const outcomes = selections.map(pricedOutcome)

	return {
		upperLimit: ceilingFor(odds, tightestLimit(limits, outcomes, 'liabilityLimit')),
		vipUpperLimit: ceilingFor(odds, tightestLimit(limits, outcomes, 'vipLiabilityLimit')),
		upperPayLimit: 0
	}
}

/**
 * The ceiling of a Betslip whose one stake covers all its Selections (Single, Combo, Bet Builder
 * Combo): the smallest of its Selections' own ceilings. 0 is "no ceiling", so it drops out; with
 * none set the result is 0 as well.
 */
export function wholeBetslipCeiling(ceilings: RiskLimitDto[]): RiskLimitDto {
	const smallest = (values: number[]): number => {
		const set = values.filter((value) => value > 0)
		return set.length === 0 ? 0 : Math.min(...set)
	}

	return {
		upperLimit: smallest(ceilings.map((c) => c.upperLimit)),
		vipUpperLimit: smallest(ceilings.map((c) => c.vipUpperLimit)),
		upperPayLimit: 0
	}
}

function pricedOutcome(priced: PricedSelection): {marketId: number, outcomeKey: string} {
	return {marketId: priced.selection.marketId, outcomeKey: priced.outcomeKey}
}

/**
 * Looks each Selection up in the offer, the way the backend does: the payload carries no
 * market id, only the `oddsId`. A Selection the offer does not know prices as unset.
 */
function priceSelections(selections: CreateBetslipSelectionDto[]): PricedSelection[] {
	return selections.map((dto) => {
		const offered = offerSelection(dto.oddsId)

		return {
			outcomeKey: offered?.outcomeKey ?? '',
			selection: {
				...(offered ?? {}),
				matchId: dto.matchId,
				oddsId: dto.oddsId,
				marketId: offered?.marketId ?? dto.marketId ?? 0,
				odds: dto.odds,
				isBanker: dto.banker ?? false,
				isPlayable: true,
				marketStatus: 0,
				outcomeStatus: 0,
				isBlocked: false,
				problems: [],
				sortIndex: 0
			} as BetslipSelection
		}
	})
}

function product(values: number[]): number {
	return values.reduce((acc, value) => acc * value, 1)
}

export function mockBetslipRiskInfo(limits: LiabilityLimit[], payload: CreateBetslipDto): BetslipRiskInfoDto {
	const priced = priceSelections(payload.selections ?? [])
	const response: BetslipRiskInfoDto = {
		problemCode: 0,
		problems: [],
		riskLimitDto: NO_CEILING,
		selections: [],
		systems: []
	}

	// Each Selection on its own odds against its own pair.
	const perSelection = priced.map((p) => ({
		matchId: p.selection.matchId,
		oddsId: p.selection.oddsId,
		problems: [],
		riskLimitDto: riskLimit(limits, p.selection.odds, [p])
	}))

	switch (payload.betType) {
		case BetType.multiSingle:
			response.selections = perSelection
			return response

		case BetType.system:
		case BetType.betBuilderSystem:
			response.systems = (payload.systems ?? [])
				.map((system) => systemRiskInfo(limits, payload.betType, priced, system))
			return response

		default:
			// Single, Combo and Bet Builder Combo
			response.selections = perSelection
			response.riskLimitDto = wholeBetslipCeiling(perSelection.map((s) => s.riskLimitDto))
			return response
	}
}

/**
 * The reply as a VIP player gets it. Priced as above, `upperLimit` is the Auto-accept tier and
 * `vipUpperLimit` the cap, which is how a normal player gets it; a VIP is accepted automatically up
 * to the VIP tier, a stake above it going for Authorization, and is not capped.
 */
export function forVipPlayer(response: BetslipRiskInfoDto): BetslipRiskInfoDto {
	const vip = (limit: RiskLimitDto | undefined): RiskLimitDto =>
		({upperLimit: limit?.vipUpperLimit ?? 0, vipUpperLimit: 0, upperPayLimit: limit?.upperPayLimit ?? 0})

	return {
		...response,
		riskLimitDto: vip(response.riskLimitDto),
		selections: response.selections.map((s) => ({...s, riskLimitDto: vip(s.riskLimitDto)})),
		systems: response.systems.map((s) => ({...s, riskLimitDto: vip(s.riskLimitDto)}))
	}
}

/**
 * A System Option spans every Selection on the Betslip, so the smallest limit binds it, as it
 * does a Combo. Its stake is spread over its Combinations and its odds are their sum, so the
 * odds its stake carries are `odds / Combinations`: with every Combination in, a stake S pays
 * `S / numberOfCombs × odds` and the operator loses `S × (odds / numberOfCombs − 1)`. System
 * N/N has one Combination and prices exactly as a Combo.
 *
 * System 1/N is its own case: one shared stake per Combination cannot give one Selection less
 * than another, so the only stake that keeps every Selection under its own ceiling is the
 * smallest per-Selection ceiling times the number of Combinations (an open question — see the
 * betslip-demo README).
 */
function systemRiskInfo(
	limits: LiabilityLimit[],
	betType: BetType,
	priced: PricedSelection[],
	system: CreateBetslipSystemDto
): SystemRiskInfoDto {
	const count = system.count ?? 0
	const bankers = priced.filter((p) => p.selection.isBanker)
	const nonBankers = priced.filter((p) => !p.selection.isBanker)

	if (count === 1 && betType === BetType.system) {
		return {count, problems: [], riskLimitDto: sharedStakeLimit(limits, bankers, nonBankers)}
	}

	const selections = priced.map((p) => p.selection)
	const strategy = betType === BetType.betBuilderSystem ? new BetBuilderSystemStrategy() : new SystemStrategy()
	const row = strategy.calculateCombinationsOdds({selections} as BetslipData).find((r) => r.count === count)
	const odds = row?.systemOdds ?? row?.maxOdds ?? 0
	const numberOfCombs = combinationCount(betType, nonBankers.map((p) => p.selection), count)

	if (odds === 0 || numberOfCombs === 0) {
		return {count, problems: [], riskLimitDto: NO_CEILING}
	}

	return {count, problems: [], riskLimitDto: riskLimit(limits, odds / numberOfCombs, priced)}
}

function combinationCount(betType: BetType, nonBankers: BetslipSelection[], count: number): number {
	if (betType === BetType.betBuilderSystem) {
		const counts = ValidCombinationsCalculator.countByGroupSizes(ValidCombinationsCalculator.groupSizesByMatch(nonBankers))
		return Number(counts[count] ?? 0n)
	}

	return Number(CombinationsCalculator.numberOfCombinations(nonBankers.length, count))
}

function sharedStakeLimit(limits: LiabilityLimit[], bankers: PricedSelection[], nonBankers: PricedSelection[]): RiskLimitDto {
	const bankersOdds = product(bankers.map((p) => p.selection.odds))
	const perSelection = nonBankers.map((p) => riskLimit(limits, bankersOdds * p.selection.odds, [...bankers, p]))
	const smallest = (key: 'upperLimit' | 'vipUpperLimit'): number => {
		const values = perSelection.map((limit) => limit[key]).filter((value) => value > 0)
		return values.length > 0 && values.length === perSelection.length
			? floor2(Math.min(...values) * nonBankers.length)
			: 0
	}

	return {upperLimit: smallest('upperLimit'), vipUpperLimit: smallest('vipUpperLimit'), upperPayLimit: 0}
}
